import React, {FunctionComponent, useContext} from "react";

import ParticipantCounter from "../common/ParticipantCounter";
import {EngageAppContext} from "../../App";
import {Theme} from "engage-client/theme";
import {createUseStyles, useTheme} from "react-jss";
import useMediaQuery from "engage-client/hooks/useMediaQuery";
import {ExpansionTimeMS, LeaderboardRowHeight, LeaderboardRowHeightCompact, PanelState} from "./accessoryConstants";
import LeaderboardRow from "../leaderboard/LeaderboardRow";
import clsx from "clsx";
import DisplayText, {TextVariant} from "engage-client/components/DisplayText";
import NotificationBubble from "../common/NotificationBubble";
import {ChatContext, ChatUIState} from "../../contexts/ChatContext";
import {TriviaStatus} from "../../state/state";

const usePlayerBannerStyles = createUseStyles<Theme>((theme) => ({
    bannerContainer: {
        backgroundColor: theme.accessoryPanel.tabs.mobileBackground ?? theme.accessoryPanel.tabs.background,
        zIndex:5,
    },

    toggleButton: {
        position:"absolute",
        left: theme.accessoryPanel.expandButton.largeButton ? 10 : 5,
        top: theme.accessoryPanel.expandButton.largeButton ? 10 : 5,
        [theme.breakpoints.up("sm")] : {
            top: 8,
        },
        width: theme.accessoryPanel.expandButton.largeButton ? 20 : 30,
        height: theme.accessoryPanel.expandButton.largeButton ? 22 : 30,
        cursor: "pointer",
        transition: `transform ${ExpansionTimeMS}ms linear`,
        transform: ({panelState:ps}) => ps === PanelState.expanded || ps === PanelState.expanding ? "rotate(180deg)" : "rotate(0deg)"
    },
    "@keyframes fadeIn": {
        from: {
            opacity: 0
        }
    },
    glow: {
        pointerEvents: "none",
        animation: "$fadeIn 1s infinite alternate"
    },
    warningLabel: {
        position:"absolute",
        left: 40,
        top: "50%",
        transform: "translate(0, -50%)",
        opacity: 0,
        transition: ({panelState:ps}) => `opacity ${ExpansionTimeMS/2}ms ease-out ${ps === PanelState.expanding || ps === PanelState.collapsed ? ExpansionTimeMS/2 : 0}ms`,
        "&.show": {
            opacity: 1
        },
        color: "white",
        fontWeight: 700
    },
    chatCounter: {
        position: "absolute",
        top:3,
        left:26,
        [theme.breakpoints.up("sm")] : {
            top: 6,
        },
        opacity: ({panelState:ps}) => ps === PanelState.collapsed || ps === PanelState.collapsing ? 1 : 0,
        transition: `opacity ${ExpansionTimeMS/2}ms linear`,
    },

    rowContainer: {
        position: "absolute",
        left: 40,
        top: 4,
        bottom: 4,
        right:0,
        opacity: ({panelState:ps}) => ps === PanelState.collapsed || ps === PanelState.collapsing ? 1 : 0,
        //Row Container should be delayed when we're expanded or collapsing
        transition: ({panelState:ps}) => `opacity ${ExpansionTimeMS/2}ms ease-out ${ps === PanelState.collapsing || ps === PanelState.expanded ? ExpansionTimeMS/2 : 0}ms`,
    },
    counter: {
        margin:10,
        [theme.breakpoints.between("sm","md")] : {
            margin: 12,
        },
        opacity: ({panelState:ps}) => ps === PanelState.expanded || ps === PanelState.expanding ? 1 : 0,
        //Counter should be delayed when we're collapsed or expanding
        transition: ({panelState:ps}) => `opacity ${ExpansionTimeMS/2}ms ease-out ${ps === PanelState.expanding || ps === PanelState.collapsed ? ExpansionTimeMS/2 : 0}ms`,
    }
}));

type PlayerPositionBannerProps = {
    toggleSlide?:()=>void
    panelState: PanelState
    showBanner?:boolean
    className?:string
    showAnswerWarning:boolean
}
export const PlayerPositionBanner:FunctionComponent<PlayerPositionBannerProps> = (props) => {
    const theme:Theme = useTheme();
    const {toggleSlide, panelState, showBanner = true, className, showAnswerWarning} = props;
    const {userState, user, appState} = useContext(EngageAppContext.current);
    const {chatUIState, chatReadCount} = useContext(ChatContext);
    const compact = useMediaQuery(theme.breakpoints.only("xs"));
    const height = compact ? LeaderboardRowHeightCompact : LeaderboardRowHeight;
    const classes = usePlayerBannerStyles({panelState, height});
    const unreadCount = chatUIState !== ChatUIState.focused ? appState.chat.entries.length - chatReadCount : 0;
    const unreadLabel = unreadCount > 99 ? "99+" : unreadCount;

    return (
        <div className={clsx(classes.bannerContainer,className)}>
            <div className={classes.rowContainer}>
                { showBanner && userState.position ?
                    <LeaderboardRow currentPlayer={true}
                     highlightColor="#04304BBA"
                     color={theme.accessoryPanel.leaderboard.row.currentPlayer.unrankedColor}
                     borderColor=""
                     compact={compact}
                     name={user?.nickname ?? ""}
                     rank={`${userState?.position}`}
                     score={userState.scores.total}
                     highlighted={false}
                     hotStreak={userState.streak >= 3}
                     rankChange={userState.lastPosition ? userState.lastPosition - (userState.position ?? 0) : 0} /> : null }
            </div>
            <ParticipantCounter className={classes.counter} showURL={false}/>
            {
                toggleSlide && appState.common.state !== TriviaStatus.Complete &&
                <img className={classes.toggleButton}
                     src={theme.accessoryPanel.expandButton.mobile.expand.src}
                     srcSet={theme.accessoryPanel.expandButton.mobile.expand.srcSet}
                     onClick={toggleSlide} alt="Toggle Leaderboard"/>
            }
            {
                showAnswerWarning &&
                <img className={clsx(classes.toggleButton, classes.glow)}
                     src={theme.accessoryPanel.expandButton.mobile.expandGlow.src}
                     srcSet={theme.accessoryPanel.expandButton.mobile.expandGlow.srcSet}
                     alt="Toggle Leaderboard"/>
            }
            {unreadCount ? <NotificationBubble className={classes.chatCounter}>{unreadLabel}</NotificationBubble> : null}
            <DisplayText variant={TextVariant.s14med} color={theme.accessoryPanel.textColor ?? "#229FC4"}
                         className={clsx(classes.warningLabel, showAnswerWarning && "show")}>
                Answer Now
            </DisplayText>
        </div>
    );
};